import { Form, FormHandlers, FormProps } from '@doveit/bricks';
import React from 'react';
import { INPUT_PLACEHOLDERS } from '../../../constants';
import { deepMerge, isKeyOf } from '../../../utils/object/object';
import hireApplicantValidationSchema from './HireApplicantForm.schema';
import { GeoFormModel } from '../../../geo/components/form-geo-field/types';
import { GEO_FORM_MODEL_DEFAULT_VALUES } from '../../../geo/components/form-geo-field/constants';
import FormGeoField from '../../../geo/components/form-geo-field/FormGeoField';
import { BooleanString } from '../../../utils/boolean-string/booleanString';
import { JobTitle } from '../../../domain/types';
import { asSelectOptions } from '../../../select-options/utils/asSelectOptions';
import { jobTitleLabels } from '../../../labels/jobTitleLabels';
import { formatInputDate } from '../../../utils/form';

export interface HireApplicantFormModel {
  workName: string,
  personalName: string,
  workSurname: string,
  personalSurname: string,
  workEmail: string,
  personalEmail: string,
  pecEmail: string,
  workMobile: string,
  personalMobile: string,
  fiscalCode: string,
  residenceAddress: GeoFormModel,
  shippingAddress: GeoFormModel,
  role: string,
  startDate: string,
  hasLicense: BooleanString,
  needsSupervisor: BooleanString,
  inTrial: boolean,
}

export interface HireApplicantFormProps {
  initialValues?: Partial<HireApplicantFormModel>,
  loading?: FormProps<HireApplicantFormModel>['loading'],
  disabled?: FormProps<HireApplicantFormModel>['disabled'],
  innerRef?: React.MutableRefObject<FormHandlers>,
  onSubmit: FormProps<HireApplicantFormModel>['onSubmit'],
}

const roleToAttributes: Record<string, {
  hasLicense?: BooleanString,
  needsSupervisor?: BooleanString,
  inTrial?: boolean,
}> = {
  '': {
    hasLicense: '',
    needsSupervisor: '',
    inTrial: false,
  },
  [JobTitle.AGENTE]: {
    hasLicense: 'true',
    inTrial: false,
  },
  [JobTitle.AREA_MANAGER]: {
    hasLicense: 'true',
    needsSupervisor: 'false',
    inTrial: false,
  },
  [JobTitle.CONSULENTE]: {
    hasLicense: 'false',
    needsSupervisor: 'true',
    inTrial: false,
  },
};

export const HIRE_APPLICANT_FORM_DEFAULT_VALUES: HireApplicantFormModel = {
  workName: '',
  personalName: '',
  workSurname: '',
  personalSurname: '',
  workEmail: '',
  personalEmail: '',
  pecEmail: '',
  workMobile: '',
  personalMobile: '',
  fiscalCode: '',
  residenceAddress: GEO_FORM_MODEL_DEFAULT_VALUES,
  shippingAddress: GEO_FORM_MODEL_DEFAULT_VALUES,
  role: '',
  startDate: '',
  hasLicense: '',
  needsSupervisor: '',
  inTrial: false,
};

const HireApplicantForm: React.FC<HireApplicantFormProps> = (props) => {
  const { initialValues = {}, innerRef, ...rest } = props;

  const toggleManualEntryForResidenceAddress = React.useMemo(
    () => !initialValues.residenceAddress?.latitude
      && !initialValues.residenceAddress?.longitude
      && !!initialValues.residenceAddress?.normalizedAddress?.description,
    [initialValues.residenceAddress?.latitude, initialValues.residenceAddress?.longitude, initialValues.residenceAddress?.normalizedAddress],
  );

  const toggleManualEntryForShippingAddress = React.useMemo(
    () => !initialValues.shippingAddress?.latitude
      && !initialValues.shippingAddress?.longitude
      && !!initialValues.shippingAddress?.normalizedAddress?.description,
    [initialValues.shippingAddress?.latitude, initialValues.shippingAddress?.longitude, initialValues.shippingAddress?.normalizedAddress?.description],
  );

  const mergedValues: HireApplicantFormModel = React.useMemo(() => deepMerge(
    HIRE_APPLICANT_FORM_DEFAULT_VALUES,
    initialValues,
  ), [initialValues]);

  return (
    <Form
      initialValues={mergedValues}
      validationSchema={hireApplicantValidationSchema}
      enableReinitialize
      innerRef={innerRef}
      {...rest}
    >
      {({ values, setFieldValue }) => {
        const onRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
          const roleSelected = e.target.value;

          if (isKeyOf(roleToAttributes, roleSelected)) {
            Object.entries(roleToAttributes[roleSelected]).forEach(([field, value]) => {
              setFieldValue(field, value, true);
            });
          }
        };

        const setToTrue = (fieldToChange: 'needsSupervisor' | 'hasLicense') => {
          const { role } = values;

          if (role === JobTitle.EXECUTIVE || role === JobTitle.TEAM_LEADER || role === JobTitle.BROKER) {
            setFieldValue(fieldToChange, 'true');
          }
        };

        const onNeedsSupervisorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          if (e.target.value === 'false') {
            setToTrue('hasLicense');
          }
        };

        const onHasLicenseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          if (e.target.value === 'false') {
            setToTrue('needsSupervisor');
          }
        };

        return (
          <>
            <Form.Fieldset legend="Ruolo e data di inizio">
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Select
                  name="role"
                  aria-label="Campo per inserire il ruolo"
                  label="Ruolo"
                  options={asSelectOptions(jobTitleLabels, {
                    label: 'Seleziona',
                    value: '',
                    disabled: true,
                  })}
                  onChange={onRoleChange}
                  required
                />
              </Form.Item>

              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  name="startDate"
                  type="date"
                  aria-label="Campo per inserire la data di inizio"
                  label="Data di inizio"
                  min={formatInputDate(new Date())}
                  required
                />
              </Form.Item>

              <Form.Item size={{ MD: 1 / 3 }}>
                <Form.YesOrNotButtons
                  label="Ha il patentino?"
                  name="hasLicense"
                  aria-label="Campo per specificare se ha il patentino"
                  disabled={['', JobTitle.AREA_MANAGER, JobTitle.AGENTE, JobTitle.CONSULENTE].includes(values.role)}
                  onChange={onHasLicenseChange}
                  required
                />
              </Form.Item>

              <Form.Item size={{ MD: 1 / 3 }}>
                <Form.YesOrNotButtons
                  label="Necessita approvazione?"
                  name="needsSupervisor"
                  aria-label="Campo per specificare se necessita di approvazione"
                  disabled={['', JobTitle.AREA_MANAGER, JobTitle.CONSULENTE].includes(values.role)}
                  onChange={onNeedsSupervisorChange}
                  required
                />
              </Form.Item>

              <Form.Item size={{ MD: 1 / 3 }}>
                <Form.CheckboxButton
                  label="&nbsp;"
                  name="inTrial"
                  text="Modalità trial"
                  aria-label="Campo per specificare se l'agente verrà assunto in modalità trial"
                  disabled={values.role !== JobTitle.AGENTE}
                />
              </Form.Item>

            </Form.Fieldset>

            <Form.Fieldset legend="Nome e cognome">
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  name="workName"
                  label="Nome"
                  aria-label="Campo per inserire il nome lavorativo dell'agente"
                  placeholder={INPUT_PLACEHOLDERS.NAME}
                  required
                />
              </Form.Item>

              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  name="workSurname"
                  label="Cognome"
                  aria-label="Campo per inserire il cognome lavorativo dell'agente"
                  placeholder={INPUT_PLACEHOLDERS.SURNAME}
                  required
                />
              </Form.Item>

              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  name="personalName"
                  label="Nome reale"
                  aria-label="Campo per inserire il nome reale dell'agente"
                  placeholder={INPUT_PLACEHOLDERS.NAME}
                />
              </Form.Item>

              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  name="personalSurname"
                  label="Cognome reale"
                  aria-label="Campo per inserire il cognome reale dell'agente"
                  placeholder={INPUT_PLACEHOLDERS.SURNAME}
                />
              </Form.Item>
            </Form.Fieldset>

            <Form.Fieldset legend="Telefono">
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  name="workMobile"
                  label="Lavorativo"
                  aria-label="Campo per inserire il telefono lavorativo dell'agente"
                  placeholder={INPUT_PLACEHOLDERS.CONTACT_PHONE_NUMBER}
                />
              </Form.Item>

              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  name="personalMobile"
                  label="Personale"
                  aria-label="Campo per inserire il telefono personale dell'agente"
                  placeholder={INPUT_PLACEHOLDERS.CONTACT_PHONE_NUMBER}
                  required
                />
              </Form.Item>
            </Form.Fieldset>

            <Form.Fieldset legend="Email">
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  name="workEmail"
                  label="Lavorativa"
                  aria-label="Campo per inserire l'email lavorativa dell'agente"
                  placeholder={INPUT_PLACEHOLDERS.DOVE_IT_EMAIL}
                />
              </Form.Item>

              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  name="personalEmail"
                  label="Personale"
                  aria-label="Campo per inserire l'email personale dell'agente"
                  placeholder={INPUT_PLACEHOLDERS.CONTACT_EMAIL}
                  required
                />
              </Form.Item>

              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  name="pecEmail"
                  label="PEC"
                  aria-label="Campo per inserire la PEC dell'agente"
                  placeholder={INPUT_PLACEHOLDERS.PEC_EMAIL}
                />
              </Form.Item>
            </Form.Fieldset>

            <Form.Fieldset legend="Indirizzi">
              <Form.Item>
                <FormGeoField
                  aria-label="Indirizzo di residenza"
                  name="residenceAddress"
                  enableManualEntry
                  toggleManualEntry={toggleManualEntryForResidenceAddress}
                  labels={{
                    normalizedAddress: 'Residenza',
                  }}
                />
              </Form.Item>

              <Form.Item>
                <FormGeoField
                  aria-label="Indirizzo di spedizione materiali"
                  name="shippingAddress"
                  enableManualEntry
                  toggleManualEntry={toggleManualEntryForShippingAddress}
                  labels={{
                    normalizedAddress: 'Invio materiali',
                  }}
                />
              </Form.Item>
            </Form.Fieldset>

            <Form.Fieldset legend="Codice fiscale">
              <Form.Item size={{ MD: 2 / 3 }}>
                <Form.Input
                  name="fiscalCode"
                  aria-label="Campo per inserire il codice fiscale dell'agente"
                  placeholder={INPUT_PLACEHOLDERS.CONTACT_FISCALCODE}
                />
              </Form.Item>
            </Form.Fieldset>
          </>
        );
      }}
    </Form>
  );
};

export default HireApplicantForm;
